import type { FinderGroup, FinderItem, FinderMatterItem } from './matterFinder.types';

/** Same tokenising as the server, so highlights line up with what matched. */
export function tokenizeFinderQuery(q: string): string[] {
  return q
    .toLowerCase()
    .split(/[^a-z0-9@.]+/)
    .map((t) => t.trim())
    .filter((t) => t.length > 0)
    .slice(0, 8);
}

/** Split `text` into [plain, hit, plain, hit…] segments for the given tokens; odd indexes are hits. */
export function highlightSegments(text: string, tokens: string[]): string[] {
  const usable = tokens.filter((t) => t.length >= 2);
  if (!text || usable.length === 0) return [text];
  const pattern = new RegExp(`(${usable.map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`, 'ig');
  return text.split(pattern);
}

export interface FlatRow {
  groupIndex: number;
  item: FinderItem;
}

/** Groups flattened to one keyboard-navigable list, in display order. */
export function flattenGroups(groups: FinderGroup[]): FlatRow[] {
  const out: FlatRow[] = [];
  groups.forEach((g, groupIndex) => g.items.forEach((item) => out.push({ groupIndex, item })));
  return out;
}

const STAGE_LABEL: Record<string, string> = {
  intake: 'Intake',
  review: 'Review',
  approved: 'Approved',
  active: 'Active',
  closed: 'Closed',
  rejected: 'Rejected',
};

export function stageLabel(stage: string): string {
  return STAGE_LABEL[stage] || stage;
}

/** "Sep 9" this year, "Sep 9, 2025" otherwise, "today"/"yesterday" when close. */
export function relativeActivity(iso: string, now = new Date()): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const days = Math.floor((now.getTime() - d.getTime()) / 86_400_000);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days} days ago`;
  const opts: Intl.DateTimeFormatOptions = d.getFullYear() === now.getFullYear() ? { month: 'short', day: 'numeric' } : { month: 'short', day: 'numeric', year: 'numeric' };
  return d.toLocaleDateString(undefined, opts);
}

/** One line under the title: number · client · practice / subtype · jurisdiction. */
export function matterMetaParts(m: FinderMatterItem, opts: { hideClient?: boolean } = {}): string[] {
  const parts: string[] = [];
  if (m.matterNumber) parts.push(m.matterNumber);
  if (!opts.hideClient && m.clientName) parts.push(m.clientName);
  const practice = [m.practiceArea, m.caseSubtypeLabel || m.caseSubtype].filter(Boolean).join(' / ');
  if (practice) parts.push(practice);
  if (m.jurisdictionCode) parts.push(m.jurisdictionCode);
  return parts;
}

const LAST_MATTER_KEY = 'edgelex.matterFinder.lastMatter';

/** Per-device memory of the matter a host linked last (Billing suggests it first). */
export function rememberLastMatter(host: string, matterId: string): void {
  try {
    const raw = window.localStorage.getItem(LAST_MATTER_KEY);
    const map = raw ? (JSON.parse(raw) as Record<string, string>) : {};
    map[host] = matterId;
    window.localStorage.setItem(LAST_MATTER_KEY, JSON.stringify(map));
  } catch {
    /* storage unavailable: nothing to remember */
  }
}

export function lastMatterFor(host: string): string | null {
  try {
    const raw = window.localStorage.getItem(LAST_MATTER_KEY);
    const map = raw ? (JSON.parse(raw) as Record<string, string>) : {};
    return typeof map[host] === 'string' ? map[host] : null;
  } catch {
    return null;
  }
}
